import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, Clock, Loader2, Utensils, Package,
  User, MapPin, FileText, Truck, Store, Inbox
} from 'lucide-react';
import { supabase } from './services/supabase';

const OrderHistoryStyles = () => (
  <style>{`
    .oh-page {
      max-width: 680px;
      margin: 0 auto;
      padding: 30px 20px;
      min-height: calc(100vh - 140px);
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
    }

    .oh-card {
      background: white;
      border-radius: 4px;
      margin-bottom: 16px;
      overflow: hidden;
      box-shadow: 0 1px 4px rgba(0,0,0,0.06);
      border: 1px solid #e5e7eb;
    }

    /* Green header */
    .oh-header {
      background: linear-gradient(135deg, #2F7D5E, #3d9970);
      padding: 14px 18px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
    }
    .oh-header-left {
      display: flex; align-items: center; gap: 8px;
      color: white; font-weight: 400; font-size: 0.95rem;
    }
    .oh-header-right {
      display: flex; align-items: center; gap: 10px;
    }
    .oh-status {
      font-size: 0.78rem; font-weight: 400;
      padding: 4px 14px; border-radius: 4px; white-space: nowrap;
    }
    .oh-status-pending { background: #fef3c7; color: #92400e; }
    .oh-status-accepted { background: #dbeafe; color: #1e40af; }
    .oh-status-preparing { background: #fef9c3; color: #854d0e; }
    .oh-status-ready { background: #d1fae5; color: #065f46; }
    .oh-status-delivering { background: #e0e7ff; color: #3730a3; }
    .oh-status-completed { background: #f1f5f9; color: #64748b; }
    .oh-status-cancelled { background: #fee2e2; color: #991b1b; }
    .oh-arrow {
      color: rgba(255,255,255,0.7); transition: transform 0.25s;
    }
    .oh-arrow.open { transform: rotate(180deg); }

    /* Body */
    .oh-body {
      padding: 16px 18px;
      cursor: pointer;
    }
    .oh-row {
      display: flex; align-items: flex-start; gap: 8px;
      padding: 5px 0; font-size: 0.9rem; color: #334155; line-height: 1.5;
    }
    .oh-row svg { color: #2F7D5E; flex-shrink: 0; margin-top: 2px; }
    .oh-label { color: #64748b; min-width: 56px; flex-shrink: 0; }
    .oh-val { color: #1e293b; font-weight: 400; }

    /* Expanded detail area — sits between body and total */
    .oh-detail {
      padding: 0 18px;
    }
    .oh-detail-divider {
      height: 1px; background: #e5e7eb; margin: 0;
    }

    /* Section titles */
    .oh-sec-title {
      display: flex; align-items: center; gap: 6px;
      font-weight: 400; font-size: 0.82rem; color: #2F7D5E;
      margin: 16px 0 10px; letter-spacing: 0.3px;
    }

    /* Item rows */
    .oh-items { display: flex; flex-direction: column; }
    .oh-item {
      display: flex; gap: 12px; padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;
    }
    .oh-item:last-child { border-bottom: none; }
    .oh-item-img {
      width: 44px; height: 44px; border-radius: 4px; object-fit: cover; flex-shrink: 0;
      background: #f8f8f8; border: 1px solid #e2e8f0;
    }
    .oh-item-img-ph {
      width: 44px; height: 44px; border-radius: 4px; flex-shrink: 0;
      background: #f1f5f9; border: 1px solid #e2e8f0;
      display: flex; align-items: center; justify-content: center; color: #cbd5e1;
    }
    .oh-item-body { flex: 1; min-width: 0; }
    .oh-item-top {
      display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;
    }
    .oh-item-name { font-weight: 400; font-size: 0.88rem; color: #1e293b; }
    .oh-item-qty { font-size: 0.85rem; color: #94a3b8; }
    .oh-item-price { font-weight: 400; font-size: 0.88rem; color: #1e293b; white-space: nowrap; }
    .oh-item-mods { margin-top: 3px; }
    .oh-mod {
      display: flex; justify-content: space-between; font-size: 0.78rem; padding: 1px 0;
    }
    .oh-mod-name { color: #64748b; }
    .oh-mod-price { color: #64748b; }
    .oh-item-note {
      font-size: 0.78rem; color: #b45309; font-style: italic; margin-top: 2px;
    }

    /* Weekly day header */
    .oh-day-hd {
      font-weight: 400; font-size: 0.82rem; color: #475569;
      margin: 14px 0 6px; padding: 6px 0; border-bottom: 1px dashed #e2e8f0;
    }
    .oh-day-hd:first-child { margin-top: 0; }
    .oh-meal-tag { font-size: 0.7rem; font-weight: 400; padding: 2px 8px; border-radius: 4px; }
    .oh-meal-tag-lunch { background: #fef3c7; color: #92400e; }
    .oh-meal-tag-dinner { background: #dbeafe; color: #1e40af; }
    .oh-time-tag {
      display: inline-flex; align-items: center; gap: 3px;
      font-size: 0.72rem; color: #64748b; background: #f1f5f9;
      padding: 2px 8px; border-radius: 4px; margin-left: 6px;
    }

    /* Fee rows */
    .oh-fees { margin-top: 12px; }
    .oh-fee-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 4px 0; font-size: 0.88rem; color: #475569;
    }
    .oh-fee-row span:last-child { font-weight: 400; }

    /* Info rows (no grey card) */
    .oh-info-section { margin-top: 16px; }
    .oh-info-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 5px 0; font-size: 0.88rem;
    }
    .oh-info-label { color: #64748b; }
    .oh-info-val { color: #1e293b; font-weight: 400; text-align: right; }
    .oh-note-val {
      color: #475569; font-size: 0.85rem; line-height: 1.5;
      white-space: pre-wrap; word-break: break-word;
    }

    /* Total bar — always at bottom */
    .oh-total-bar {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px 18px;
      border-top: 2px solid #e5e7eb;
    }
    .oh-total-label {
      font-size: 0.95rem; font-weight: 400; color: #475569;
    }
    .oh-total-val {
      font-size: 1.2rem; font-weight: 400; color: #2F7D5E;
    }

    /* Empty + loading */
    .oh-empty {
      text-align: center; padding: 80px 20px; color: #94a3b8;
    }
    .oh-empty-icon { margin-bottom: 16px; color: #cbd5e1; }
    .oh-loading {
      text-align: center; padding: 80px 20px; color: #94a3b8;
    }
    .oh-spinner { animation: oh-spin 1s linear infinite; margin: 0 auto 16px; display: block; }
    @keyframes oh-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    @media (max-width: 640px) {
      .oh-page { padding: 16px 12px; }
      .oh-header { padding: 12px 14px; }
      .oh-body { padding: 14px; }
      .oh-detail { padding: 0 14px; }
      .oh-total-bar { padding: 14px; }
    }
  `}</style>
);

const formatScheduledTime = (rawTime) => {
  if (!rawTime) return '';
  const parts = rawTime.split(' ');
  if (parts.length === 2 && parts[0].includes('-')) {
    const [, month, day] = parts[0].split('-');
    return `${parseInt(month)}/${parseInt(day)} ${parts[1]}`;
  }
  return rawTime;
};

const STATUS_MAP = {
  pending: 'oh-status-pending',
  accepted: 'oh-status-accepted',
  preparing: 'oh-status-preparing',
  ready: 'oh-status-ready',
  delivering: 'oh-status-delivering',
  completed: 'oh-status-completed',
  cancelled: 'oh-status-cancelled',
};

const OrderHistoryPage = () => {
  const { t, i18n } = useTranslation('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const locale = i18n.language === 'en' ? 'en-US' : 'zh-TW';
  const isEn = i18n.language === 'en';
  const getItemName = (item) => isEn ? (item.name_en || item.name) : (item.name_zh || item.name);
  const getModName = (m) => isEn ? (m.name_en || m.name) : (m.name_zh || m.alternate_name || m.name);

  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        setUserId(user.id);

        const { data, error } = await supabase
          .from('orders')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setOrders(data || []);
      } catch (err) {
        setError(t('error'));
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [t]);

  // Realtime: listen for status updates on user's orders
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel('user-order-updates')
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders', filter: `user_id=eq.${userId}` },
        (payload) => {
          setOrders(prev => prev.map(o =>
            o.id === payload.new.id ? { ...o, ...payload.new } : o
          ));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  const toggleExpand = (orderId) => {
    setExpandedOrderId(prev => prev === orderId ? null : orderId);
  };

  const getItemsPreview = (order) => {
    const isCartOrder = !!order.items_data?.cart_items;
    if (isCartOrder) {
      const items = order.items_data.cart_items || [];
      return items.map(item => {
        const name = getItemName(item);
        return item.quantity > 1 ? `${name} x${item.quantity}` : name;
      }).join(', ');
    }
    const itemsData = order.items_data || {};
    const names = [];
    Object.values(itemsData).forEach(day => {
      if (day.lunch) names.push(getItemName(day.lunch));
      if (day.dinner) names.push(getItemName(day.dinner));
    });
    return names.slice(0, 3).join(', ') + (names.length > 3 ? '...' : '');
  };

  const getItemCount = (order) => {
    const isCartOrder = !!order.items_data?.cart_items;
    if (isCartOrder) {
      return (order.items_data.cart_items || []).reduce((s, item) => s + (item.quantity || 1), 0);
    }
    let count = 0;
    Object.values(order.items_data || {}).forEach(day => {
      if (day.lunch) count++;
      if (day.dinner) count++;
    });
    return count;
  };

  const getStatusLabel = (status) => {
    const map = {
      pending: t('status.pending', '待處理'),
      accepted: t('status.accepted', '已接單'),
      preparing: t('status.preparing', '準備中'),
      ready: t('status.ready', '可取餐'),
      delivering: t('status.delivering', '配送中'),
      completed: t('status.completed', '已完成'),
      cancelled: t('status.cancelled', '已取消'),
    };
    return map[status] || status;
  };

  const renderMealItem = (meal, type, time) => {
    if (!meal) return null;
    return (
      <div className="oh-item">
        {meal.image_url ? (
          <img src={meal.image_url} alt={meal.name} className="oh-item-img" loading="lazy" decoding="async"
            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
        ) : (
          <div className="oh-item-img-ph"><Utensils size={18} /></div>
        )}
        <div className="oh-item-body">
          <div className="oh-item-top">
            <span>
              <span className={`oh-meal-tag ${type === 'lunch' ? 'oh-meal-tag-lunch' : 'oh-meal-tag-dinner'}`}>
                {type === 'lunch' ? t('meal.lunch') : t('meal.dinner')}
              </span>
              {time && <span className="oh-time-tag"><Clock size={11} /> {time}</span>}
            </span>
            <span className="oh-item-price">${(meal.price || 0).toFixed(2)}</span>
          </div>
          <div className="oh-item-name" style={{ marginTop: 4 }}>{getItemName(meal)}</div>
          {meal.modifiers?.length > 0 && (
            <div className="oh-item-mods">
              {meal.modifiers.map((m, idx) => (
                <div className="oh-mod" key={idx}>
                  <span className="oh-mod-name">+ {getModName(m)}</span>
                  {Number(m.price || 0) > 0 && <span className="oh-mod-price">+${Number(m.price).toFixed(2)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderScheduleItems = (order) => {
    const itemsData = order.items_data || {};
    const scheduleData = order.delivery_schedule || {};
    const dayIds = Object.keys(itemsData).sort((a, b) => parseInt(a) - parseInt(b));
    const orderCreatedDate = new Date(order.created_at);

    return (
      <div className="oh-items">
        {dayIds.map(dayId => {
          const day = itemsData[dayId];
          let dateStr = '';
          if (day.date_info?.full_date) {
            dateStr = `${day.date_info.full_date} (${day.date_info.weekday})`;
          } else {
            const d = new Date(orderCreatedDate);
            d.setDate(orderCreatedDate.getDate() + parseInt(dayId));
            dateStr = d.toLocaleDateString(locale, { month: 'long', day: 'numeric', weekday: 'short' });
          }
          return (
            <div key={dayId}>
              <div className="oh-day-hd">{dateStr}</div>
              {renderMealItem(day.lunch, 'lunch', scheduleData[`${dayId}_lunch`])}
              {renderMealItem(day.dinner, 'dinner', scheduleData[`${dayId}_dinner`])}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCartItems = (order) => {
    const items = order.items_data?.cart_items || [];
    return (
      <div className="oh-items">
        {items.map((item, idx) => {
          const modTotal = (item.modifiers || []).reduce((s, m) => s + Number(m.price || 0), 0);
          const lineTotal = ((Number(item.price) || 0) + modTotal) * (item.quantity || 1);
          return (
            <div className="oh-item" key={idx}>
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className="oh-item-img" loading="lazy" decoding="async"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
              ) : (
                <div className="oh-item-img-ph">{item.emoji || <Utensils size={18} />}</div>
              )}
              <div className="oh-item-body">
                <div className="oh-item-top">
                  <span>
                    <span className="oh-item-name">{getItemName(item)}</span>
                    <span className="oh-item-qty"> x{item.quantity || 1}</span>
                  </span>
                  <span className="oh-item-price">${lineTotal.toFixed(2)}</span>
                </div>
                {item.modifiers?.length > 0 && (
                  <div className="oh-item-mods">
                    {item.modifiers.map((m, mi) => (
                      <div className="oh-mod" key={mi}>
                        <span className="oh-mod-name">+ {getModName(m)}</span>
                        {Number(m.price || 0) > 0 && <span className="oh-mod-price">+${Number(m.price).toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {item.note && (
                  <div className="oh-item-note">{t('item_note', '備註')}: {item.note}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderExpandedDetail = (order) => {
    const isCartOrder = !!order.items_data?.cart_items;
    const isPickup = order.delivery_info?.order_type === 'pickup';
    const di = order.delivery_info || {};
    const ci = order.contact_info || {};
    const deliveryFee = Number(order.totals_data?.delivery_fee || order.delivery_fee || 0);
    const fulfillment = di.fulfillment_mode;

    return (
      <div className="oh-detail">
        <div className="oh-detail-divider" />

        {/* Order type info — first */}
        <div className="oh-info-section">
          <div className="oh-sec-title">
            {isPickup ? <Store size={16} /> : <Truck size={16} />}
            {t('order_type.title', '訂單類型')}
          </div>
          <div className="oh-info-row">
            <span className="oh-info-label">{t('order_type.method', '取餐方式')}</span>
            <span className="oh-info-val">
              {order.booking_mode === 'weekly'
                ? t('order_type.weekly', '每周配餐')
                : (isPickup ? t('order_type.pickup', '自取') : t('order_type.delivery', '配送'))}
            </span>
          </div>
          {fulfillment && (
            <div className="oh-info-row">
              <span className="oh-info-label">{t('order_type.fulfillment', '取餐類型')}</span>
              <span className="oh-info-val">
                {fulfillment === 'instant'
                  ? (isPickup ? t('order_type.instant_pickup', '立即自取') : t('order_type.instant_delivery', '立即配送'))
                  : t('order_type.scheduled', '預約')}
              </span>
            </div>
          )}
          {fulfillment === 'scheduled' && di.scheduled_time && (
            <div className="oh-info-row">
              <span className="oh-info-label">{isPickup ? t('order_type.scheduled_pickup_time', '預約取餐') : t('order_type.scheduled_delivery_time', '預約送達')}</span>
              <span className="oh-info-val">{formatScheduledTime(di.scheduled_time)}</span>
            </div>
          )}
          {di.need_cutlery !== undefined && (
            <div className="oh-info-row">
              <span className="oh-info-label">{t('order_type.cutlery', '是否需要餐具')}</span>
              <span className="oh-info-val" style={{color: di.need_cutlery ? '#2F7D5E' : '#ef4444', fontWeight: 500}}>
                {di.need_cutlery ? t('order_type.cutlery_yes', '需要') : t('order_type.cutlery_no', '不需要')}
              </span>
            </div>
          )}
        </div>

        {/* Contact / delivery info — second */}
        {(ci.name || di.address || di.delivery_notes) && (
          <div className="oh-info-section">
            <div className="oh-sec-title">
              {isPickup ? <User size={16} /> : <MapPin size={16} />}
              {isPickup ? t('contact.title', '聯絡資訊') : t('delivery.title', '配送資訊')}
            </div>
            {ci.name && (
              <div className="oh-info-row">
                <span className="oh-info-label">{t('delivery.name', '姓名')}</span>
                <span className="oh-info-val">{ci.name}</span>
              </div>
            )}
            {!isPickup && di.address && (
              <div className="oh-info-row">
                <span className="oh-info-label">{t('delivery.address', '地址')}</span>
                <span className="oh-info-val">{di.address}{di.unit ? ` #${di.unit}` : ''}</span>
              </div>
            )}
            {di.delivery_notes && (
              <div style={{padding: '5px 0'}}>
                <span className="oh-info-label" style={{fontSize:'0.88rem'}}>{t('delivery.notes', '備註')}</span>
                <div className="oh-note-val" style={{marginTop: 2}}>{di.delivery_notes}</div>
              </div>
            )}
          </div>
        )}

        {/* Items — third */}
        <div className="oh-sec-title">
          <Package size={16} />
          {isCartOrder ? t('items.cart_title', '訂單內容') : t('items.schedule_title', '餐點排程')}
        </div>
        {isCartOrder ? renderCartItems(order) : renderScheduleItems(order)}

        {/* Fees */}
        <div className="oh-fees">
          {(order.discount > 0 || order.totals_data?.discount > 0) ? (
            <>
              <div className="oh-fee-row">
                <span>{t('fees.original_subtotal', '原價小計')}</span>
                <span style={{textDecoration:'line-through', color:'#94a3b8'}}>${(order.totals_data?.original_subtotal || 0).toFixed(2)}</span>
              </div>
              <div className="oh-fee-row" style={{color:'#e53e3e'}}>
                <span>{order.totals_data?.discount_label || t('fees.discount', '全單折扣')}{order.totals_data?.discount_rate ? ` (${Math.round(order.totals_data.discount_rate * 100)}折)` : ''}</span>
                <span>-${(order.discount || order.totals_data?.discount || 0).toFixed(2)}</span>
              </div>
              <div className="oh-fee-row">
                <span>{t('fees.discounted_subtotal', '折後小計')}</span>
                <span>${(order.subtotal || 0).toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="oh-fee-row">
              <span>{t('fees.subtotal', '餐點小計')}</span>
              <span>${(order.totals_data?.subtotal || order.subtotal || 0).toFixed(2)}</span>
            </div>
          )}
          {!isPickup && (
            <div className="oh-fee-row">
              <span>{t('fees.delivery_fee', '配送運費')}</span>
              <span>{deliveryFee === 0 ? t('fees.free', '免費') : `$${deliveryFee.toFixed(2)}`}</span>
            </div>
          )}
          {Number(order.totals_data?.packaging_fee || 0) > 0 && (
            <div className="oh-fee-row">
              <span>{t('fees.packaging_fee', '外賣包裝費')}</span>
              <span>${Number(order.totals_data.packaging_fee).toFixed(2)}</span>
            </div>
          )}
          <div className="oh-fee-row">
            <span>GST (5%)</span>
            <span>${(order.totals_data?.gst || order.gst || 0).toFixed(2)}</span>
          </div>
          {Number(order.totals_data?.pst || order.pst || 0) > 0 && (
            <div className="oh-fee-row">
              <span>PST (7%)</span>
              <span>${Number(order.totals_data?.pst || order.pst || 0).toFixed(2)}</span>
            </div>
          )}
        </div>

        <div style={{height: 8}} />
      </div>
    );
  };

  if (loading) return (
    <>
      <OrderHistoryStyles />
      <div className="oh-page">
        <div className="oh-loading">
          <Loader2 className="oh-spinner" size={36} />
          <p>{t('loading')}</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      <OrderHistoryStyles />
      <div className="oh-page">
        {orders.length > 0 ? (
          orders.map((order) => {
            const isExpanded = expandedOrderId === order.id;
            const isPickup = order.delivery_info?.order_type === 'pickup';
            const total = order.totals_data?.total_amount || order.total_amount || 0;
            const displayId = order.order_number || `ORD-${order.id}`;
            const statusClass = STATUS_MAP[order.status] || 'oh-status-pending';

            const dateStr = new Date(order.created_at).toLocaleString(locale, {
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit'
            });

            return (
              <div key={order.id} className="oh-card">
                {/* Green header */}
                <div className="oh-header" onClick={() => toggleExpand(order.id)}>
                  <div className="oh-header-left">
                    {isPickup ? <Store size={18} /> : <Truck size={18} />}
                    <span>{isPickup ? t('order_type.pickup', '自取訂單') : t('order_type.delivery', '配送訂單')}</span>
                  </div>
                  <div className="oh-header-right">
                    <ChevronDown size={18} className={`oh-arrow ${isExpanded ? 'open' : ''}`} />
                  </div>
                </div>

                {/* Basic info */}
                <div className="oh-body" onClick={() => toggleExpand(order.id)}>
                  <div className="oh-row" style={{justifyContent:'space-between'}}>
                    <div style={{display:'flex', alignItems:'center', gap: 8}}>
                      <FileText size={16} />
                      <span className="oh-label">{t('order_number', '訂單')}</span>
                      <span className="oh-val">#{displayId}</span>
                    </div>
                    <span className={`oh-status ${statusClass}`}>{getStatusLabel(order.status)}</span>
                  </div>
                  <div className="oh-row">
                    <Clock size={16} />
                    <span className="oh-label">{t('date', '日期')}</span>
                    <span className="oh-val">{dateStr}</span>
                  </div>
                </div>

                {/* Expanded: items, fees, info — above total */}
                {isExpanded && renderExpandedDetail(order)}

                {/* Total bar — always at bottom */}
                <div className="oh-total-bar">
                  <span className="oh-total-label">{t('fees.total', '總計')}</span>
                  <span className="oh-total-val">CAD$ {Number(total).toFixed(2)}</span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="oh-empty">
            <div className="oh-empty-icon"><Inbox size={48} /></div>
            <p>{t('empty')}</p>
          </div>
        )}
      </div>
    </>
  );
};

export default OrderHistoryPage;
